import React, { useState, useContext, useCallback } from "react";
import { CartContext } from "../pages/CartPage/CartContext";

/**
 * BestCollection Component
 * Displays best-selling or top-rated products in a horizontal carousel
 * Positioned right after the morph tabs
 */

const BestCollection = ({ products = [], navigate }) => {
  const { addToCart } = useContext(CartContext);
  const [animatedButtonId, setAnimatedButtonId] = useState(null);

  const handleAddToCart = useCallback((product, event) => {
    event.stopPropagation();
    const productId = product._id || product.id;
    const cartProduct = {
      id: productId,
      name: product.name,
      price: Number(product.price || 0),
      image: product.image || product.imageUrl || "",
      description: product.description || "",
      categoryId: product.categoryId || product.category || "",
      checkinDT: !!product.checkinDT,
      multiDayDiscountDays: Number(product.multiDayDiscountDays || 0),
      multiDayDiscountPercent: Number(product.multiDayDiscountPercent || 0),
      quantity: 1,
    };
    addToCart(cartProduct);
    setAnimatedButtonId(productId);
    setTimeout(() => setAnimatedButtonId(null), 300);
  }, [addToCart]);

  const goToDetail = (product) => {
    if (typeof navigate === "function") navigate("productDetail", product._id || product.id);
  };

  if (!products || products.length === 0) return null;

  return (
    <section className="bg-gradient-to-r from-blue-50 to-cyan-50 py-8 relative z-10 mt-6">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="mb-6">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
            Best Collection
          </h2>
          <p className="text-gray-600 text-sm mt-1">Our top-selling and highest-rated products</p>
        </div>

        {/* Horizontal Scroll Container */}
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-4 pb-4 min-w-max">
            {products.slice(0, 12).map((product) => {
              const price = Number(product.price || 0);
              const mrp = Number(product.mrp || 0);
              const showMrp = mrp > 0 && mrp > price;
              const discountPercent = showMrp ? Math.round(((mrp - price) / mrp) * 100) : 0;
              const productId = product._id || product.id;

              return (
                <div
                  key={productId}
                  className="flex-shrink-0 w-56 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group border border-gray-100"
                  onClick={() => goToDetail(product)}
                >
                  {/* Image Container */}
                  <div className="relative h-40 bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center overflow-hidden">
                    {showMrp && discountPercent > 0 && (
                      <div className="absolute top-2 left-2 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-lg shadow-md">
                        {discountPercent}% OFF
                      </div>
                    )}
                    {product.image || product.imageUrl ? (
                      <img
                        src={product.image || product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://placehold.co/400x300/E0E7FF/4F46E5?text=${(product.name || "Product").replace(/\s/g, "+")}`;
                        }}
                      />
                    ) : (
                      <div className="text-4xl">📦</div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-900 text-sm line-clamp-1 mb-1">
                      {product.name}
                    </h3>
                    <p className="text-xs text-gray-500 line-clamp-1 mb-2">{product.description}</p>

                    {/* Rating */}
                    <div className="flex items-center gap-0.5 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className="w-3 h-3 text-amber-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-xs text-gray-400 ml-0.5">4.5</span>
                    </div>

                    {/* Price and Button */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-col">
                        {showMrp && (
                          <span className="text-xs text-gray-400 line-through">₹{mrp}</span>
                        )}
                        <span className="text-sm font-bold text-blue-600">₹{price.toFixed(0)}</span>
                      </div>
                      <button
                        onClick={(e) => handleAddToCart(product, e)}
                        className={`bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-1 flex-shrink-0 ${
                          animatedButtonId === productId ? "scale-110" : ""
                        }`}
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tailwind utility for hiding scrollbar */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default BestCollection;
